import { useFetchPage } from "~/core/composables/useFetchPage";
import { SearchMode, useSearch } from "~/core/composables/useSearch";
import { useSort } from "~/core/composables/useSort";
import type { UseFetchParameters } from "~/core/models/UseFetchParameters.type";
import type { UseFetchResponse } from "~/core/models/UseFetchResponse.interface";
import {
  Filters,
  type FiltersSelection,
  FiltersType,
  OPERATOR_RSQL_ANY_MATCH,
  OPERATOR_RSQL_ILIKE,
  OPERATOR_RSQL_IN,
} from "~/core/models/filters";
import filtersUtils from "~/core/utils/filters.utils";
import type { CampaignOperationMgr } from "~/modules/campaign/models/CampaignOperationMgr.interface";
import CampaignOperationMgrService from "~/modules/campaign/services/campaignOperationMgr.service";

export function useFetchCampaigns({
  organizationCode,
}: UseFetchParameters): UseFetchResponse<CampaignOperationMgr> {
  const service = CampaignOperationMgrService.findWithPagination.bind(
    CampaignOperationMgrService,
  );

  const appliedFilters: FiltersSelection = new Map([
    [
      Filters.NAME,
      {
        key: "name",
        operator: OPERATOR_RSQL_ILIKE,
        value: "",
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
    [
      Filters.SECTORS,
      {
        key: "operationSectorCodeList",
        operator: OPERATOR_RSQL_ANY_MATCH,
        value: "",
      },
    ],
    [
      Filters.CAMPAIGN_TYPE,
      {
        key: "operationTypes",
        operator: OPERATOR_RSQL_ANY_MATCH,
        value: [],
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
    [
      Filters.CAMPAIGN_STATUS,
      {
        key: "status",
        operator: OPERATOR_RSQL_IN,
        value: [],
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
  ]);

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.RSQL,
    appliedFilters,
    SearchMode.URL_SEARCH,
  );
  const { sort, onSortChange } = useSort();
  const { isLoading, results, error, fetchData } = useFetchPage({
    service,
    searchParams,
    sort,
    useQueryParametersForPagination: true,
    organizationCode,
  });

  return {
    isLoading,
    error,
    results,
    filters,
    fetchData,
    setFilter,
    onSortChange,
    search,
    onSearch,
    sort,
  };
}
